import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Coins } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageTitle, CHANNELS, TYPES } from "@/components/app/ui";
import { useAuth } from "@/context/AuthContext";
import { api, streamPost } from "@/lib/api";

const COSTS = { social_post: 5, email: 8, ad_copy: 6, blog: 20, webinar: 8, seo_brief: 10 };
const PREMIUM = { blog: "seo", seo_brief: "seo", webinar: "campaigns" };

const Pick = ({ label, value, onChange, opts, testId }) => (
  <div className="space-y-2"><Label>{label}</Label>
    <Select value={value} onValueChange={onChange}><SelectTrigger data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent>{opts.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select>
  </div>
);

export default function Studio() {
  const [params] = useSearchParams();
  const { company, refresh } = useAuth();
  const [f, setF] = useState({ content_type: "social_post", channel: "LinkedIn", model: "auto", topic: "", extra: "", persona: "" });
  const [personas, setPersonas] = useState([]);
  const [out, setOut] = useState("");
  const [busy, setBusy] = useState(false);
  const [doneId, setDoneId] = useState(null);
  const outRef = useRef(null);
  const regenId = params.get("regenerate");

  useEffect(() => {
    const t = params.get("topic");
    if (t) setF((s) => ({ ...s, topic: t, content_type: params.get("type") || s.content_type }));
  }, [params]);

  useEffect(() => {
    if (!regenId) return;
    api.get("/app/contents").then((r) => {
      const c = r.data.find((x) => x.id === regenId);
      if (c) setF((s) => ({ ...s, content_type: c.type, channel: c.channel, topic: c.topic || c.title }));
    });
  }, [regenId]);

  const mods = company?.modules || [];
  useEffect(() => {
    if (mods.includes("personas")) api.get("/app/personas").then((r) => setPersonas(r.data.latest?.personas || [])).catch(() => {});
  }, [mods.length]); // eslint-disable-line
  const effModel = f.model === "auto" ? (f.content_type === "blog" ? "claude" : "gemini") : f.model;
  const costOf = (t) => (effModel === "claude" ? COSTS[t] : Math.max(1, Math.ceil(COSTS[t] / 2)));
  const typeOpts = TYPES.map(([v, l]) => [v, `${l}${PREMIUM[v] && !mods.includes(PREMIUM[v]) ? " (upgrade)" : ""} · ${COSTS[v]} cr`]);

  const run = async (e) => {
    e.preventDefault();
    setBusy(true); setOut(""); setDoneId(null);
    try {
      const res = await streamPost("/app/ai/generate", { ...f, content_id: regenId || null }, (d) => {
        setOut((o) => o + d);
        if (outRef.current) outRef.current.scrollTop = outRef.current.scrollHeight;
      });
      setDoneId(res?.content_id);
      toast.success(`Draft saved for approval · ${res?.credits_charged} credits used`);
      refresh();
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  return (
    <div data-testid="studio-page">
      <PageTitle title="AI Content Studio" copy="Generate on-brand drafts grounded in your Brand Profile and Knowledge Base. Every draft goes to Approvals before publishing." />
      <div className="grid lg:grid-cols-12 gap-6">
        <form onSubmit={run} className="lg:col-span-5 card-box p-6 space-y-4 h-fit">
          <Pick label="Content type" value={f.content_type} onChange={(v) => setF({ ...f, content_type: v })} opts={typeOpts} testId="studio-type-select" />
          <div className="grid grid-cols-2 gap-4">
            <Pick label="Channel" value={f.channel} onChange={(v) => setF({ ...f, channel: v })} opts={CHANNELS.map((c) => [c, c])} testId="studio-channel-select" />
            <Pick label="AI model" value={f.model} onChange={(v) => setF({ ...f, model: v })} opts={[["auto", "Auto · lowest cost"], ["gemini", "Gemini 3 Flash · 50% credits"], ["claude", "Claude Sonnet 4.5"]]} testId="studio-model-select" />
          </div>
          {personas.length > 0 && <Pick label="Target persona (Customer Persona Agent)" value={f.persona || "_any"} onChange={(v) => setF({ ...f, persona: v === "_any" ? "" : v })}
            opts={[["_any", "All audiences"], ...personas.map((p) => [`${p.name}: ${p.summary || ""}`.slice(0, 300), p.name])]} testId="studio-persona-select" />}
          <div className="space-y-2"><Label>Topic / brief</Label><Textarea required minLength={2} rows={4} value={f.topic} onChange={(e) => setF({ ...f, topic: e.target.value })} placeholder="e.g., Launch of our new 250 TR energy-efficient screw chiller for pharma plants" data-testid="studio-topic-input" /></div>
          <div className="space-y-2"><Label>Extra instructions (optional)</Label><Input value={f.extra} onChange={(e) => setF({ ...f, extra: e.target.value })} placeholder="e.g., include 3 hashtags, under 120 words" data-testid="studio-extra-input" /></div>
          <div className="flex items-center justify-between rounded-xl bg-foreground/5 px-4 py-3 text-sm" data-testid="studio-cost-estimate">
            <span className="flex items-center gap-2"><Coins className="h-4 w-4 text-[#f59e0b]" /> Estimated cost</span><strong>{costOf(f.content_type)} credits</strong>
          </div>
          <button disabled={busy} className="btn-brand w-full disabled:opacity-60" data-testid="studio-generate-btn"><Sparkles className="h-4 w-4" />{busy ? "Generating..." : regenId ? "Regenerate draft" : "Generate draft"}</button>
        </form>
        <div className="lg:col-span-7 card-box p-6 flex flex-col min-h-[460px]">
          <div className="flex items-center justify-between"><h3 className="font-semibold">Output</h3>{busy && <span className="font-mono text-[10px] uppercase text-[#7c3aed] animate-pulse">Streaming…</span>}</div>
          <div ref={outRef} className="mt-4 flex-1 overflow-y-auto rounded-xl bg-foreground/5 p-5 whitespace-pre-wrap text-sm leading-relaxed max-h-[560px]" data-testid="studio-output">
            {out || <span className="text-muted-foreground">Your AI draft will appear here.</span>}
          </div>
          {doneId && <Link to="/app/approvals" className="btn-ghost mt-4 self-start" data-testid="studio-review-btn">Review in Approvals</Link>}
        </div>
      </div>
    </div>
  );
}
